import { useAppSelector } from "../store/configureStore";
import { InstoreTemplate } from "../models/instoreTemplate";
import useActiveWeekInstoreTemplateForStore from "./useActiveWeekInstoreTemplateForStore";
import useActiveWeekInstoreTemplates from "./useActiveWeekInstoreTemplates";
import usePickerManager from "./usePickerManager";
import useAdminManager from "./useAdminManager";
import useHQmanager from "./useHQmanager";

// Which active "Week" templates the current user may see on the projects page:
// HQ / picker / admin see one per instore type, a shop user only the one for its
// own store format. Lives in a hook because both the tab bar (is the NON promo
// tab empty?) and the list itself need the exact same answer — deriving it twice
// invites the two drifting apart.
//
// `loaded` reports whether the underlying fetches settled, so callers can tell
// "nothing available" apart from "not fetched yet" and avoid flagging an empty
// list during the first render.
export default function useVisibleWeekTemplates(): {
  templates: InstoreTemplate[];
  seesAllTypes: boolean;
  loaded: boolean;
} {
  const { user } = useAppSelector((state) => state.account);
  const { activeWeekTemplatesLoaded } = useAppSelector(
    (state) => state.instoreTemplates
  );
  const { instoreTypes_Loaded } = useAppSelector((state) => state.instoreTypes);

  const isPicker = usePickerManager(user);
  const isAdmin = useAdminManager(user);
  const hqTeam = useHQmanager(user);

  const activeWeekTemplates = useActiveWeekInstoreTemplates();
  const storeWeekTemplate = useActiveWeekInstoreTemplateForStore(
    user?.store?.store_format_id
  );

  const seesAllTypes = hqTeam || isPicker || isAdmin;

  return {
    templates: seesAllTypes
      ? activeWeekTemplates
      : storeWeekTemplate
        ? [storeWeekTemplate]
        : [],
    seesAllTypes,
    // A shop user's template is resolved through store format → instore type,
    // so for that role the instore types have to be in as well.
    loaded: seesAllTypes
      ? activeWeekTemplatesLoaded
      : activeWeekTemplatesLoaded && instoreTypes_Loaded,
  };
}
